import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import {
  CreditAgreementStore,
  UseCreditAgreementStore,
  CreateUseCreditAgreementStore,
} from './store';
import { useCreditAgreementServerSync } from './useCreditAgreementServerSync';
import { useFetchCreditAgreement } from './useFetchCreditAgreement';
import { usePreventAccidentalNavigation } from './usePreventAccidentalNavigation';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';

export type CreditAgreementContextValue = {
  useStore: UseCreditAgreementStore;
};

const FacilityContext = createContext<CreditAgreementContextValue | undefined>(
  undefined,
);

export const useCreditAgreementContext = () => {
  const context = useContext(FacilityContext);
  if (context === undefined) {
    throw new Error(
      'useFacilityContext must be used within a CreditAgreementProvider',
    );
  }
  return context;
};

export const useCreditAgreementStore = <T,>(
  selector: (state: CreditAgreementStore) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T => {
  const { useStore } = useCreditAgreementContext();
  return useStore(selector, equalityFn);
};

// This is a hook which returns a stable reference to a function that
// can be called in an imperative manner to get the creditAgreement.
// This is useful when we don't want components to rerender when the
// credit agreement changes - when setting defaultValues on forms for instance.
export const useGetCreditAgreementLazy = () => {
  const { useStore } = useCreditAgreementContext();
  return useCallback(() => {
    const { creditAgreement } = useStore.getState();
    finleyAssert(creditAgreement, 'CreditAgreement must not be null');
    return creditAgreement;
  }, [useStore]);
};

type CreditAgreementProviderBaseProps = {
  children: React.ReactNode;
  facilityId: string;
};
const CreditAgreementProviderBase = ({
  children,
  facilityId,
}: CreditAgreementProviderBaseProps) => {
  const useStore = useMemo(CreateUseCreditAgreementStore, [facilityId]);

  useFetchCreditAgreement(useStore, facilityId);
  useCreditAgreementServerSync(useStore, facilityId);
  usePreventAccidentalNavigation(useStore, facilityId);

  const contextValue: CreditAgreementContextValue = useMemo(
    () => ({
      useStore,
    }),
    [useStore],
  );

  return (
    <FacilityContext.Provider value={contextValue}>
      {children}
    </FacilityContext.Provider>
  );
};

type CreditAgreementProviderProps = { children: React.ReactNode };
export const CreditAgreementProvider = ({
  children,
}: CreditAgreementProviderProps) => {
  const params = useParams();
  const { facilityId } = FacilityDetailsRouteParamsStruct.parse(params);

  return (
    <CreditAgreementProviderBase facilityId={facilityId}>
      {children}
    </CreditAgreementProviderBase>
  );
};
